import { useFormik } from 'formik';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Button, Input, Select } from 'ui';
import Switch from 'ui/Switch';

import { ReactComponent as BellIcon } from 'assets/images/icons/bell.svg';

import { authApi } from 'services/auth';
import {
  dialCodeToIso2,
  formatPhoneByMask,
  getDialCode,
  getPhonePlaceholder,
  phoneCodeOptions,
} from 'utils/countries';

import { QUERY_PARAMS } from 'utils/constants';
import { QueryHelper } from 'utils/helpers';
import { PAGES } from 'utils/link';
import { filterEmailInput, filterPasswordInput } from 'utils/validation';

import { IRootStore } from 'store/Root.reducer';
import { setStepOneData as sendStepOneData, setUserId } from 'store/UserCreate/UserCreate.action';
import {
  resetUserUpdateData,
  setStepOneData as editStepOneData,
} from 'store/UserUpdate/UserUpdate.action';
import { IStepOneForm } from 'store/UserUpdate/UserUpdate.type';

import { validationSchemaCreateStepOne, validationSchemaEditStepOne } from './validationSchema';

type PropTypes = {
  id?: string;
};

const UserCreateStepOne: FC<PropTypes> = ({ id }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const stepOneData = useSelector(
    (store: IRootStore) => store[id ? 'userUpdate' : 'userCreate'].stepOne,
  );

  const phoneCodeValue = useMemo(() => {
    const current = stepOneData.phoneCode;
    if (phoneCodeOptions?.some((opt) => opt.value === current)) {
      return current;
    }
    return dialCodeToIso2(current);
  }, [stepOneData.phoneCode]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [initialState, setInitialState] = useState<IStepOneForm | undefined>(undefined);

  const form = useFormik({
    initialValues: stepOneData,
    validationSchema: !!id ? validationSchemaEditStepOne : validationSchemaCreateStepOne,
    enableReinitialize: true,
    onSubmit: async (values: typeof stepOneData) => {
      if (isSubmitting) return;

      if (!id) {
        try {
          setIsSubmitting(true);

          const whatsappNumber = values.phoneNumber
            ? getDialCode(values.phoneCode) + values.phoneNumber.replace(/\D/g, '')
            : undefined;

          const response = await authApi.registerUser({
            email: values.email.trim(),
            password: values.password,
            fullname: values.fullname || undefined,
            whatsapp_number: whatsappNumber,
            whatsapp_notifications_enabled: values.whatsappNotifications,
          });

          dispatch(sendStepOneData(values));
          dispatch(setUserId(response.id));
          setIsSubmitting(false);
          navigate(
            PAGES.USER_CREATE +
              QueryHelper.getQueryString({
                [QUERY_PARAMS.STEP]: 2,
              }),
          );
        } catch (error: any) {
          console.error('Error creating user:', error);
          const detail = error?.response?.data?.detail;
          if (typeof detail === 'string' && detail.toLowerCase().includes('email')) {
            form.setFieldError('email', detail);
          } else {
            form.setFieldError('email', detail || 'Failed to create user. Please try again.');
          }
          setIsSubmitting(false);
        }
      } else {
        try {
          setIsSubmitting(true);

          const whatsappNumber = values.phoneNumber
            ? getDialCode(values.phoneCode) + values.phoneNumber.replace(/\D/g, '')
            : undefined;

          await authApi.updatePersonalInfo(parseInt(id), {
            email: values.email.trim(),
            ...(values.password && { password: values.password }),
            fullname: values.fullname || undefined,
            whatsapp_number: whatsappNumber,
            whatsapp_notifications_enabled: values.whatsappNotifications,
          });

          dispatch(editStepOneData(values));
          dispatch(resetUserUpdateData());
          if (id) {
            localStorage.removeItem(`userUpdateData_${id}`);
          }
          setIsSubmitting(false);
          navigate(PAGES.ADMIN_USERS, { replace: true });
        } catch (error: any) {
          console.error('Failed to update personal info:', error);
          const detail = error?.response?.data?.detail;
          if (typeof detail === 'string' && detail.toLowerCase().includes('email')) {
            form.setFieldError('email', detail);
          } else {
            form.setFieldError(
              'email',
              detail || 'Failed to save personal info. Please try again.',
            );
          }
          setIsSubmitting(false);
        }
      }
    },
  });

  const { setFieldValue } = form;

  const handleEmailChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const filtered = filterEmailInput(e.target.value);
      setFieldValue('email', filtered, false);
    },
    [setFieldValue],
  );

  const handlePasswordChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const filtered = filterPasswordInput(e.target.value);
      setFieldValue('password', filtered, false);
    },
    [setFieldValue],
  );

  const handleFullNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      let value = e.target.value;
      if (value.length > 100) {
        value = value.substring(0, 100);
      }
      setFieldValue('fullname', value, false);
    },
    [setFieldValue],
  );

  const handleFullNameBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const trimmed = e.target.value.trim();
      setFieldValue('fullname', trimmed, false);
      form.handleBlur(e);
    },
    [setFieldValue, form],
  );

  const handlePhoneCodeChange = useCallback(
    (value: string) => {
      setFieldValue('phoneCode', value, false);
      setFieldValue('phoneNumber', '', false);
    },
    [setFieldValue],
  );

  const handlePhoneNumberChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value.replace(/\D/g, '');
      const formatted = formatPhoneByMask(raw, form.values.phoneCode);
      setFieldValue('phoneNumber', formatted, false);

      if (formatted.length > 0 && !form.values.whatsappNotifications) {
        setFieldValue('whatsappNotifications', true, false);
      }
      if (formatted.length === 0 && form.values.whatsappNotifications) {
        setFieldValue('whatsappNotifications', false, false);
      }
    },
    [setFieldValue, form.values.phoneCode, form.values.whatsappNotifications],
  );

  const handleWhatsappToggle = useCallback(
    (checked: boolean) => {
      setFieldValue('whatsappNotifications', checked, false);
    },
    [setFieldValue],
  );

  const phonePlaceholder = useMemo(
    () => getPhonePlaceholder(phoneCodeValue) || '',
    [phoneCodeValue],
  );

  useEffect(() => {
    if (form.dirty && !id) {
      dispatch(sendStepOneData(form.values));
    }
  }, [form.values, form.dirty, id, dispatch]);

  useEffect(() => {
    if (form.dirty && !!id) {
      dispatch(editStepOneData(form.values));
    }
  }, [form.values, form.dirty, id, dispatch]);

  useEffect(() => {
    if (!initialState && stepOneData) {
      setInitialState(stepOneData);
    }
  }, [initialState, stepOneData]);

  return (
    <form onSubmit={form.handleSubmit} className="user-create__form" autoComplete="off">
      <Input
        name="fullname"
        type="text"
        label="Full name"
        placeholder="Full name"
        value={form.values.fullname}
        onChange={handleFullNameChange}
        onBlur={handleFullNameBlur}
        maxLength={100}
        error={form.touched.fullname && form.errors.fullname ? form.errors.fullname : undefined}
      />

      <Input
        name="user-email"
        type="email"
        autoComplete="off"
        label="Email"
        placeholder="Email"
        value={form.values.email}
        onChange={handleEmailChange}
        onBlur={form.handleBlur}
        maxLength={256}
        error={form.touched.email && form.errors.email ? form.errors.email : undefined}
      />

      <Input
        name="user-password"
        type="password"
        autoComplete="new-password"
        label={id ? 'Create new password' : 'Password'}
        placeholder="Password"
        value={form.values.password}
        onChange={handlePasswordChange}
        onBlur={form.handleBlur}
        maxLength={64}
        error={form.touched.password && form.errors.password ? form.errors.password : undefined}
      />

      <div className="user-create__phone-group">
        <p className="user-create__phone-label">WhatsApp number</p>
        <div className="user-create__phone-row">
          <Select
            name="phoneCode"
            label="Code"
            value={phoneCodeValue}
            options={phoneCodeOptions}
            onChange={handlePhoneCodeChange}
            wrapperClassName="user-create__phone-code"
            dropdownClassName="user-create__phone-code-dropdown"
            placeholder="Search"
            withSearch
          />
          <Input
            name="phoneNumber"
            type="tel"
            label="Phone number"
            placeholder={phonePlaceholder}
            value={form.values.phoneNumber}
            onChange={handlePhoneNumberChange}
            onBlur={form.handleBlur}
            wrapperClassName="user-create__phone-number"
            error={
              form.touched.phoneNumber && form.errors.phoneNumber
                ? form.errors.phoneNumber
                : undefined
            }
          />
        </div>
        <p className="user-create__phone-hint">
          This number will be used to send notifications when a zone needs attention
        </p>
      </div>

      <div className="user-create__notification-card">
        <span className="user-create__notification-icon">
          <BellIcon />
        </span>
        <div className="user-create__notification-text">
          <p className="user-create__notification-title">WhatsApp notifications</p>
          <p className="user-create__notification-description">
            User will receive alerts about zone issues
          </p>
        </div>
        <Switch
          id="whatsappNotifications"
          checked={form.values.whatsappNotifications}
          onChange={handleWhatsappToggle}
        />
      </div>

      <Button type="submit" fullWidth size="large" disabled={isSubmitting}>
        {id ? 'Save' : 'Continue'}
      </Button>
    </form>
  );
};

export default UserCreateStepOne;
